/**
 * AuthClient Type Definitions
 *
 * Type definitions for the AWS Cognito AuthClient implementation.
 * These types define the interface for authentication operations using direct AWS SDK.
 */

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface CognitoUserData {
  username: string;
  email: string;
  userId: string;
  attributes: Record<string, string>;
  groups?: string[];
}

export interface AuthClientConfig {
  userPoolId: string;
  clientId: string;
  region: string;
}

export interface SignInParams {
  username: string;
  password: string;
}

export interface SignUpParams {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organization: string;
}

export interface ConfirmSignUpParams {
  username: string;
  code: string;
}

export interface ForgotPasswordParams {
  username: string;
}

export interface ConfirmForgotPasswordParams {
  username: string;
  code: string;
  newPassword: string;
}

export interface ChangePasswordParams {
  oldPassword: string;
  newPassword: string;
}

export interface UpdateUserAttributesParams {
  attributes: Record<string, string>;
}

export interface AuthClientInterface {
  signIn(params: SignInParams): Promise<CognitoTokens>;

  signUp(params: SignUpParams): Promise<{ userSub: string }>;

  confirmSignUp(params: ConfirmSignUpParams): Promise<void>;

  forgotPassword(params: ForgotPasswordParams): Promise<void>;

  confirmForgotPassword(params: ConfirmForgotPasswordParams): Promise<void>;

  changePassword(params: ChangePasswordParams): Promise<void>;

  signOut(): Promise<void>;

  getCurrentUser(): Promise<CognitoUserData | null>;

  getValidAccessToken(): Promise<string>;

  refreshSession(): Promise<CognitoTokens>;

  updateUserAttributes(params: UpdateUserAttributesParams): Promise<void>;

  listGroups(): Promise<string[]>;
}

export interface TokenCache {
  tokens: CognitoTokens | null;
  user: CognitoUserData | null;
}

export interface SRPAuthParams {
  username: string;
  password: string;
  userPoolId: string;
  clientId: string;
}

export interface SRPAuthResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

export class AuthError extends Error {
  public code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

export type AuthErrorCode =
  | 'UserNotFoundException'
  | 'NotAuthorizedException'
  | 'UserNotConfirmedException'
  | 'PasswordResetRequiredException'
  | 'CodeMismatchException'
  | 'ExpiredCodeException'
  | 'InvalidParameterException'
  | 'TooManyRequestsException'
  | 'NetworkError'
  | 'UnknownError';
